/**
 * Sub-componente: Menu Item Desktop
 * Renderiza item do menu principal conforme design dos prints
 */

'use client';

import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { IMenuData } from '@/services/header/type';
import { HEADER_COLORS } from './headerColors';

interface MenuItemProps {
  group: IMenuData['menuGroups'][0];
  isHovered: boolean;
  isDarkText: boolean;
  onMouseEnter: () => void;
  onMouseLeave: () => void;
}

/**
 * Verifica se o menu está ativo baseado na URL atual
 * Também verifica se algum submenu está ativo (ativa o menu pai)
 */
function isMenuActive(group: IMenuData['menuGroups'][0], pathname: string): boolean {
  // Verifica se a URL do menu corresponde à página atual
  if (group.url && pathname === group.url) {
    return true;
  }

  // Verifica se algum submenu está ativo
  if (group.subMenu && group.subMenu.length > 0) {
    for (const menu of group.subMenu) {
      if (menu.subMenu && menu.subMenu.length > 0) {
        for (const subItem of menu.subMenu) {
          if (subItem.url && pathname === subItem.url) {
            return true; // Ativa o menu pai se submenu está ativo
          }
        }
      }
    }
  }

  return false;
}

/**
 * Classe do menu item baseado em tema, hover e estado ativo
 */
function getMenuItemClass(isHovered: boolean, isDarkText: boolean, isActive: boolean): string {
  const textColor = isDarkText
    ? HEADER_COLORS.text.dark
    : HEADER_COLORS.text.light;

  // Se está ativo ou com hover, usa a cor amarela #E6D660
  if (isActive || isHovered) {
    return `cursor-pointer uppercase transition-colors py-2 px-0 text-sm font-medium tracking-wide text-[#E6D660] hover:text-[#E6D660]`;
  }

  return `cursor-pointer uppercase transition-colors py-2 px-0 text-sm font-medium tracking-wide text-[${textColor}] hover:text-[#E6D660]`;
}

export function MenuItem({
  group,
  isHovered,
  isDarkText,
  onMouseEnter,
  onMouseLeave,
}: MenuItemProps) {
  const pathname = usePathname();
  const isActive = isMenuActive(group, pathname);
  const itemClass = getMenuItemClass(isHovered, isDarkText, isActive);

  const content = <span className={itemClass}>{group.label}</span>;

  return (
    <li
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      className="relative"
    >
      {group?.url ? (
        <Link href={group.url}>{content}</Link>
      ) : (
        content
      )}
    </li>
  );
}